import React from 'react';

interface SkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  className = '',
}) => {
  const baseClass = 'animate-pulse bg-gray-200 dark:bg-gray-700';

  const variantClass = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  }[variant];

  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height || (variant === 'text' ? '14px' : undefined),
  };

  return <div className={`${baseClass} ${variantClass} ${className}`} style={style} />;
};

export const ChatListSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="divide-y divide-gray-100">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="p-4 flex items-start space-x-3">
        <Skeleton variant="circular" width={40} height={40} />
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <Skeleton variant="text" width="50%" height={14} />
            <Skeleton variant="text" width={40} height={10} />
          </div>
          <Skeleton variant="text" width="75%" height={12} />
        </div>
      </div>
    ))}
  </div>
);

export const UserProfileSkeleton: React.FC = () => (
  <div className="space-y-6 p-4">
    <div className="flex items-center space-x-4">
      <Skeleton variant="circular" width={64} height={64} />
      <div className="space-y-2 flex-1">
        <Skeleton variant="text" width="40%" height={20} />
        <Skeleton variant="text" width="30%" height={14} />
      </div>
    </div>
    <div className="space-y-3">
      <Skeleton variant="rectangular" width="100%" height={40} />
      <Skeleton variant="rectangular" width="100%" height={40} />
      <Skeleton variant="rectangular" width="100%" height={40} />
    </div>
    <div className="grid grid-cols-3 gap-4">
      <Skeleton variant="rectangular" width="100%" height={60} />
      <Skeleton variant="rectangular" width="100%" height={60} />
      <Skeleton variant="rectangular" width="100%" height={60} />
    </div>
  </div>
);

export const BookingCardSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Skeleton variant="circular" width={32} height={32} />
            <div className="space-y-1">
              <Skeleton variant="text" width={120} height={14} />
              <Skeleton variant="text" width={80} height={12} />
            </div>
          </div>
          <Skeleton variant="rectangular" width={60} height={24} className="rounded-full" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton variant="text" width="40%" height={12} />
          <Skeleton variant="text" width={50} height={12} />
        </div>
      </div>
    ))}
  </div>
);
